import { useState, useEffect, useRef } from "react";
import "./App.css";

// ── Data ──────────────────────────────────────────────────────────────────
const SKILLS = [
  { category: "Cloud & Infrastructure", items: ["AWS Lambda", "API Gateway", "DynamoDB", "SQS", "S3", "CloudFront", "CDK", "IAM"] },
  { category: "Backend", items: ["Node.js", "REST APIs", "Microservices", "Event-driven architecture"] },
  { category: "Frontend", items: ["React", "JavaScript", "HTML & CSS", "Responsive design"] },
  { category: "Tools & Other", items: ["Git", "AWS CLI", "Linux", "Meditation"] },
];

const PROJECTS = [
  {
    id: "ecommerce",
    title: "E-Commerce Microservices",
    tag: "AWS · Node.js · CDK",
    description: "Production style microservices app built on AWS Lambda, API Gateway, DynamoDB, and SQS. Three independent services — products, orders, and payments. Communicating via event-driven messaging.",
    highlights: ["Serverless Lambda functions", "SQS async event flow", "Infrastructure as Code with CDK", "React storefront"],
    link: "https://connorlakour.com/shop",
    live: true,
  },
  {
  id: "space",
  title: "Space Data Dashboard",
  tag: "AWS · NASA APIs · Lambda",
  description: "A real time space data dashboard powered by NASA APIs. Browse the Astronomy Picture of the Day, track near-earth asteroids, and search over 140,000 NASA images and videos.",
  highlights: ["EventBridge scheduled data ingestion", "DynamoDB caching layer", "NASA image library search", "Space themed React dashboard"],
  link: "https://connorlakour.com/space",
  live: true,
},
  {
    id: "coming1",
    title: "More coming soon",
    tag: "In progress",
    description: "Next project in the pipeline. Check back soon.",
    highlights: [],
    link: null,
    live: false,
  },
];

// ── Hooks ─────────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Components ────────────────────────────────────────────────────────────
function Nav({ active }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = ["About", "Skills", "Projects", "Contact"];

  const scrollTo = (id) => {
    document.getElementById(id.toLowerCase())?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <nav className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
      <div className="nav-inner">
        <span className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>CL</span>
        <ul className={`nav-links ${menuOpen ? "open" : ""}`}>
          {links.map((l) => (
            <li key={l}><button onClick={() => scrollTo(l)}>{l}</button></li>
          ))}
        </ul>
        <button className="nav-burger" onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero" id="about">
      <div className="hero-bg">
        <div className="hero-grid" />
        <div className="hero-glow" />
      </div>
      <div className="hero-content">
        <p className="hero-eyebrow">Hello, I'm</p>
        <h1 className="hero-name">Connor<br />Lakour</h1>
        <p className="hero-tagline">Creator and maintainer of things.</p>
        <p className="hero-bio">
          Tech architect by day, green thumb and spiritual seeker by nature.  I'm a builder/creator who crafts scalable AWS applications with the same care I give to living soil and inner growth. With a background spanning SAP systems, web development, AWS, and spiritual practice, I bring an engineer's precision and a conscious warrior's heart to every problem I touch.
        </p>
        <div className="hero-cta">
          <button className="btn-primary" onClick={() => document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" })}>
            View my work
          </button>
          <button className="btn-ghost" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>
            Get in touch
          </button>
        </div>
      </div>
      <div className="hero-scroll">
        <span />
      </div>
    </section>
  );
}

function Skills() {
  const [ref, inView] = useInView();
  return (
    <section className="section skills-section" id="skills" ref={ref}>
      <div className="container">
        <div className={`section-header ${inView ? "visible" : ""}`}>
          <p className="section-label">What I work with</p>
          <h2 className="section-title">Skills & tech stack</h2>
        </div>
        <div className="skills-grid">
          {SKILLS.map((group, gi) => (
            <div
              key={group.category}
              className={`skill-group ${inView ? "visible" : ""}`}
              style={{ animationDelay: `${gi * 0.1}s` }}
            >
              <h3 className="skill-category">{group.category}</h3>
              <ul className="skill-list">
                {group.items.map((item) => (
                  <li key={item} className="skill-tag">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Projects() {
  const [ref, inView] = useInView();
  return (
    <section className="section projects-section" id="projects" ref={ref}>
      <div className="container">
        <div className={`section-header ${inView ? "visible" : ""}`}>
          <p className="section-label">What I've Created</p>
          <h2 className="section-title">Projects</h2>
        </div>
        <div className="projects-grid">
          {PROJECTS.map((p, i) => (
            <div
              key={p.id}
              className={`project-card ${!p.live ? "project-card--dim" : ""} ${inView ? "visible" : ""}`}
              style={{ animationDelay: `${i * 0.12}s` }}
            >
              <div className="project-card-inner">
                <div className="project-top">
                  <span className="project-tag">{p.tag}</span>
                  {p.live && <span className="project-live">Live</span>}
                </div>
                <h3 className="project-title">{p.title}</h3>
                <p className="project-desc">{p.description}</p>
                {p.highlights.length > 0 && (
                  <ul className="project-highlights">
                    {p.highlights.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                )}
                {p.link && (
                  <a href={p.link} className="project-link" target="_blank" rel="noreferrer">
                    View project
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [apiError, setApiError] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim() || form.message.length < 10) e.message = "Message is too short";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSending(true);
    setApiError(null);
    try {
      const res = await fetch("https://2q5sqtupea.execute-api.us-east-1.amazonaws.com/prod/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setSent(true);
    } catch {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section contact-section" id="contact" ref={ref}>
      <div className="container">
        <div className={`section-header ${inView ? "visible" : ""}`}>
          <p className="section-label">Let's talk</p>
          <h2 className="section-title">Get in touch</h2>
        </div>
        <div className={`contact-wrap ${inView ? "visible" : ""}`}>
          {sent ? (
            <div className="contact-sent">
              <div className="sent-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <h3>Message sent!</h3>
              <p>Thanks for reaching out. I'll get back to you soon.</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {apiError && (
                <p style={{ color: "#ef4444", marginBottom: 16, fontSize: 14 }}>{apiError}</p>
              )}
              <div className="form-row">
                <label className="form-field">
                  <span>Name</span>
                  <input
                    type="text"
                    placeholder="Jane Smith"
                    value={form.name}
                    className={errors.name ? "err" : ""}
                    onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: undefined }); }}
                  />
                  {errors.name && <span className="form-err">{errors.name}</span>}
                </label>
                <label className="form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    className={errors.email ? "err" : ""}
                    onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors({ ...errors, email: undefined }); }}
                  />
                  {errors.email && <span className="form-err">{errors.email}</span>}
                </label>
              </div>
              <label className="form-field">
                <span>Message</span>
                <textarea
                  rows={5}
                  placeholder="Tell me about your project..."
                  value={form.message}
                  className={errors.message ? "err" : ""}
                  onChange={(e) => { setForm({ ...form, message: e.target.value }); setErrors({ ...errors, message: undefined }); }}
                />
                {errors.message && <span className="form-err">{errors.message}</span>}
              </label>
              <button type="submit" className="btn-primary" disabled={sending}>
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <p className="footer-name">Connor Lakour</p>
        <p className="footer-copy">Built with React · Deployed on AWS S3 + CloudFront</p>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className="app">
      <Nav />
      <Hero />
      <Skills />
      <Projects />
      <Contact />
      <Footer />
    </div>
  );
}
